import React, { useCallback, useState } from 'react';

import { Button } from '@core/js/components/common/Button';
import { Image } from '@core/js/components/common/Image';
import { Link } from '@core/js/components/common/Link';

import { withComponent } from '@core/js/components/hocs/withComponent';
import { TParseConfig } from '@core/js/components/hocs/withComponent/types';
import { SLIDE_ACTION_TYPES } from '@core/js/components/main/Banner/components/Slide/constants';
import { useRunSlideAction } from '@core/js/components/main/Banner/components/Slide/hooks';
import { useComponentConfig } from '@core/js/hooks/component';
import { useTranslations } from '@core/js/hooks/i18n';
import { parseHTMLAttributes } from '@core/js/utils/getHTMLParserOptions.util';

import { clsx } from '@interlayer/js/utils/classNames.util';

import { IBannerConfig, IBannerProps } from './types';

import style from './Banner.scss';

const getSlideAction = ({ link, gameCode }: { link?: string; gameCode?: string }) => {
    let slideAction = SLIDE_ACTION_TYPES.none;

    if (link || gameCode) {
        slideAction = gameCode
            ? SLIDE_ACTION_TYPES.gameLaunch
            : SLIDE_ACTION_TYPES.url;
    }

    return slideAction;
};

export const BannerBare: React.FC<IBannerProps> = ({ children }) => {
    const {
        link,
        gameCode,
        imageURL,
        terms,
        alt,
        buttonText = '',
        bgColor,
        shouldShowButton,
        beGambleaWare
    } = useComponentConfig<IBannerConfig>();
    const t = useTranslations();
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const runSlideAction = useRunSlideAction();

    const slideAction = getSlideAction({ link, gameCode });
    const beGambleaWareLink = beGambleaWare?.link || t('playtech.mobile.banner.beGambleaWare.link');

    const checkTextFromData = (text: string, alternative: string): string =>
        text?.split('-')?.[0] === '$promo'
            ? alternative
            : text;

    const handleSlideClick = useCallback((event: React.SyntheticEvent<HTMLElement>) => {
        if (slideAction === SLIDE_ACTION_TYPES.none) {
            event.stopPropagation();
            event.preventDefault();
        }

        runSlideAction({
            slideAction,
            slideActionURL: link,
            slideActionGameCode: gameCode
        });
    }, [gameCode, link, runSlideAction, slideAction]);

    return (
        <div
            className={clsx(style.container, 'cms-banner-container')}
            style={{ backgroundColor: bgColor }}
        >
            {children}

            <div className={clsx(style.wrapper, 'cms-banner-wrapper')}>
                <Image
                    src={imageURL}
                    alt={checkTextFromData(alt, t('playtech.mobile.banner.img.alt.text'))}
                    onClickCapture={handleSlideClick}
                    onLoad={() => setIsImageLoaded(true)}
                    className={clsx(style.image, 'cms-banner-image', { [style.isLink]: slideAction !== SLIDE_ACTION_TYPES.none })}
                />

                {shouldShowButton && (
                    <Button
                        className={clsx(style.button, 'cms-banner-button')}
                        onClick={handleSlideClick}
                    >
                        {checkTextFromData(buttonText, t('playtech.mobile.banner.button.text'))}
                    </Button>
                )}

                <div className={clsx(style.termsContainer, 'cms-banner-terms-container', { hidden: !isImageLoaded })}>
                    <div className={clsx(style.termsContainerTextBlock, 'cms-banner-terms-text')}>
                        {t('playtech.mobile.banner.terms.template.text', [terms])}

                        {slideAction !== SLIDE_ACTION_TYPES.gameLaunch && (
                            <Link
                                href={link}
                                className={clsx(style.link, 'cms-banner-terms-link')}
                                target="_self"
                            >
                                {t('playtech.mobile.banner.terms.text')}
                            </Link>
                        )}

                        {' '}

                        {beGambleaWareLink && (
                            <Link
                                href={beGambleaWareLink}
                                className={clsx(style.link, 'cms-banner-beGambleaWare-link')}
                                target="_self"
                            >
                                {beGambleaWare?.text || t('playtech.mobile.banner.beGambleaWare.text')}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Banner = withComponent({ parseConfig: parseHTMLAttributes as TParseConfig })(BannerBare);

export default Banner;
